import { createRouter, createWebHistory } from 'vue-router'
import Tracker from '@shared/Tracker'
import useCorporateStore from '@shared/stores/useCorporateStore.js'
import { fromAppToStorage } from '@shared/helpers/isFromApp'
import env from '@shared/env.js'
import i18n from '@shared/localization'
import { setPageTitle } from '@shared/utils'
import useAuthStore from '@shared/stores/useAuthStore.js'
import appModuleRouter from '@us/app/router/appModule'
import assessmentModuleRouter from '@shared/modules/assessment/router'
import useSharedStore from '@shared/stores/useSharedStore.js'
import useLandingPageStore from '@shared/stores/useLandingPageStore.js'
import useFeatureFlagStore from '@shared/stores/useFeatureFlagStore'
import moduleRouteMatches from '@shared/router/moduleRouteMatches'
import oneTimeTokenGuard from '@shared/router/oneTimeTokenGuard'

const initRouter = () => {
  const authStore = useAuthStore()
  const featureFlagStore = useFeatureFlagStore()

  const routerModules = [appModuleRouter, assessmentModuleRouter]

  const router = createRouter({
    routes: [
      // aggregate all routes from all modules
      ...routerModules.flatMap((module) => module.routes),
      // define a catch-all route
      {
        path: '/:pathMatch(.*)*',
        redirect: {
          name: appModuleRouter.ROUTE.PARTNER_SEARCH,
          params: {},
        },
      },
    ],
    linkActiveClass: 'open active',
    scrollBehavior: (to, from, savedPosition) =>
      savedPosition || { x: 0, y: 0 },
    history: createWebHistory(env.VITE_PUBLIC_PATH),
  })

  router.beforeEach(async (routeTo, routeFrom) => {
    // Reset title on every route change; specific views can overwrite it on mount.
    setPageTitle(i18n.global.t('b2b__landing_page__generic__page_title'))

    featureFlagStore.applyQueryOverrides(routeTo.query)

    if (routeTo.query.from_app) {
      fromAppToStorage()

      const { client_app_id, client_version } = routeTo.query

      authStore.hostClientAppId = Array.isArray(client_app_id)
        ? client_app_id[0]
        : client_app_id
      authStore.hostClientVersion = Array.isArray(client_version)
        ? client_version[0]
        : client_version
    }

    const tokenTarget = await oneTimeTokenGuard(routeTo)
    if (tokenTarget !== undefined) return tokenTarget

    for (const module of routerModules) {
      // Match against the resolved route tree so nested child routes can run module hooks.
      if (moduleRouteMatches(module.routes, routeTo)) {
        return module.hooks(routeTo, routeFrom)
      }
    }
  })

  router.afterEach((routeTo, routeFrom, failure) => {
    if (failure) return

    // Initialize Pinia stores for router hooks
    const corporateStore = useCorporateStore()
    const sharedStore = useSharedStore()
    const landingPageStore = useLandingPageStore()

    Tracker.trackPageView(routeTo.fullPath, {
      ...sharedStore.utmData,
      landing_page_variant:
        landingPageStore.landingPage?.variant ||
        sharedStore.initialQuery?.variant,
      corporate:
        landingPageStore.landingPage?.slug || corporateStore.corporate?.key,
      corporate_channel: corporateStore.corporate?.corporateChannel,
    })

    if (routeTo.meta && routeTo.meta.name) {
      const detailedViewScreenRouteNames = [
        'onb_eligibility_verification',
        'onb_account_creation',
        'corporate_landing_page',
        'simplified_corporate_landing_page',
      ]
      if (detailedViewScreenRouteNames.includes(routeTo.meta.name)) {
        Tracker.trackViewScreen(routeTo.meta.name, {
          utm_medium: sharedStore.utmData?.medium || null,
          utm_source: sharedStore.utmData?.source || null,
          utm_campaign: sharedStore.utmData?.campaign || null,
          utm_content: sharedStore.utmData?.content || null,
        })
      } else {
        Tracker.trackViewScreen(routeTo.meta.name)
      }
    }
  })

  // ensure that the first page_view that is ever tracked on a session contains the initial Href
  // (this is needed for proper utm tracking)
  Tracker.trackPageView(window.location.href)

  return router
}

export default initRouter
